/**
 * Shared hook + module-level cache for the admin Missing-Fish baseline
 * floor toggle. Fetched once per session (cached at module scope so
 * multiple mounts don't hammer the endpoint) and refreshed via
 * `mutateBaselineSettings()` after the Admin Console panel writes.
 *
 * Contract for consumers:
 *   const { enabled, floor, ready } = useAdminMissingFishBaseline(isAdmin);
 *
 *   - enabled: whether the site-wide floor is currently applied. Falls
 *     back to `true` when the user is admin and the endpoint has not
 *     yet resolved so the baseline is never accidentally hidden mid-load.
 *   - floor: the numeric baseline (defaults to 10 000).
 *   - ready: true once the initial fetch has completed (or failed).
 *
 * Non-admin callers get `{ enabled: false, floor: 10000, ready: true }`
 * so downstream code never applies the baseline to a non-admin viewer.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

let _cache = null; // { enabled, floor, fetchedAt }
let _inFlight = null;
const _listeners = new Set();

const DEFAULT_FLOOR = 10000;

const notify = () => {
  for (const cb of _listeners) {
    try { cb(); } catch { /* no-op */ }
  }
};

async function fetchOnce(token) {
  if (_cache) return _cache;
  if (_inFlight) return _inFlight;
  _inFlight = (async () => {
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/admin/settings/missing-fish-baseline`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      _cache = {
        enabled: !!r.data?.enabled,
        floor: Number.isFinite(r.data?.floor) ? Number(r.data.floor) : DEFAULT_FLOOR,
        fetchedAt: Date.now(),
      };
    } catch {
      // Fail-open: the feature was ON at rollout, keep it ON when the
      // fetch fails so admins don't silently lose visibility.
      _cache = { enabled: true, floor: DEFAULT_FLOOR, fetchedAt: Date.now() };
    } finally {
      _inFlight = null;
      notify();
    }
    return _cache;
  })();
  return _inFlight;
}

/** Called by the Admin Console panel after a successful PUT to refresh
 *  every mounted consumer without a full page reload. */
export function mutateBaselineSettings(next) {
  _cache = {
    enabled: !!next?.enabled,
    floor: Number.isFinite(next?.floor) ? Number(next.floor) : DEFAULT_FLOOR,
    fetchedAt: Date.now(),
  };
  notify();
}

export function useAdminMissingFishBaseline(isAdmin, token) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!isAdmin || !token) return undefined;
    const listener = () => setTick((t) => t + 1);
    _listeners.add(listener);
    fetchOnce(token);
    return () => { _listeners.delete(listener); };
  }, [isAdmin, token]);

  if (!isAdmin) return { enabled: false, floor: DEFAULT_FLOOR, ready: true };
  if (!_cache) return { enabled: true, floor: DEFAULT_FLOOR, ready: false };
  return { enabled: _cache.enabled, floor: _cache.floor, ready: true, _tick: tick };
}
